import { Box, Button, HStack, Spinner, Text, useDisclose, VStack } from 'native-base';
import React, { ReactNode, useMemo, useState } from 'react';

import HTML from 'app/components/HTML';
import Calendar from 'app/components/icons/Calendar';
import Clock from 'app/components/icons/Clock';
import Location from 'app/components/icons/Location';
import Person from 'app/components/icons/Person';
import Sunset from 'app/components/icons/Sunset';
import RadioChoiceDialog from 'app/components/RadioChoiceDialog';
import ReservationDetailOption from 'app/components/ReservationSummary/ReservationDetailOption';
import ReservationDetails from 'app/components/ReservationSummary/ReservationDetails';
import ReservationIconDetail from 'app/components/ReservationSummary/ReservationIconDetail';
import TicketCarouselModal from 'app/components/TicketCarouselModal';
import useAttraction from 'app/hooks/useAttraction';
import useReservationTextTranslationHelper from 'app/hooks/useReservationTextTranslationHelper';
import useRootNavigation from 'app/hooks/useRootNavigation';
import { useI18n } from 'app/providers/I18nProvider';
import { useTravelPartyContext } from 'app/providers/TravelPartyProvider';
import Analytics, { Events } from 'app/services/Analytics';
import {
  EntitlementReservation,
  Reservation,
  TicketCategory,
} from 'app/services/GuestCenterService.types';
import Logger from 'app/services/Logger';
import * as ReservationHelper from 'app/services/ReservationHelper';
import { tID } from 'app/services/TestHelper';
import { isAddonTicket } from 'app/services/TicketHelper';
import { areAllPassesExpiredWithinOneWeek } from 'app/utils/time';

const CONTAINER_COLOR = 'brand.secondary';
const CONTAINER_ROUNDED = 'md';

export type ReservationSummaryProps = {
  children?: ReactNode;
  reservation: Reservation;
  attractionContentKey: string;
  orderNumber: string;
  variant?: 'default' | 'complete';
  onModifyCancel?: (res: EntitlementReservation, mode: 'modifyCancel' | 'cancelOnly') => void;
};

export default function ReservationSummary({
  orderNumber,
  reservation: { reservationKey },
  onModifyCancel,
  variant = 'default',
}: ReservationSummaryProps) {
  const navigation = useRootNavigation();
  const { t } = useI18n();
  const { reservations, activeEntitlement } = useTravelPartyContext();

  const reservation =
    activeEntitlement?.reservations?.find((res) => res.reservationKey === reservationKey) ??
    reservations?.find((res) => res.reservationKey === reservationKey);

  const { attractionContent, productContent } = useAttraction({
    productContentKey: reservation?.productLineup.contentKey ?? '',
    attractionContentKey: reservation?.attractionConfig.contentKey ?? '',
  });

  const rt = useReservationTextTranslationHelper({
    attraction: attractionContent,
    product: productContent,
  });
  const ticketsModal = useDisclose();
  const ticketCategoryDialog = useDisclose();
  const [selectedTicketCategory, setSelectedTicketCategory] = useState<TicketCategory | undefined>(
    undefined
  );
  const isUpgradeReservation = !!reservation && ReservationHelper.isUpgradeReservation(reservation);

  const upgradeBannerText = rt('upgrade_banner_text', { fallback: false });

  const allPassesExpiredWithinOneWeek = areAllPassesExpiredWithinOneWeek(activeEntitlement);
  const hasAttractionOrderNumber = !!reservation?.attractionOrderNumber;
  const canShowViewTickets = hasAttractionOrderNumber && !allPassesExpiredWithinOneWeek;

  const canCancel = ReservationHelper.canCancel(reservation);
  const canModify = ReservationHelper.canModify(reservation);

  const showBtns = !allPassesExpiredWithinOneWeek && (hasAttractionOrderNumber || canCancel);
  const canShowCancelModifyButtons = ReservationHelper.canShowCancelModifyButtons(reservation);

  const ticketSummaries = useMemo(
    () => ReservationHelper.getTicketSummaries(reservation, { rt }) ?? [],
    [reservation, rt]
  );

  if (!productContent || !attractionContent || !reservation) {
    return (
      <Box
        bg={variant === 'complete' ? 'white' : CONTAINER_COLOR}
        py="24"
        rounded={CONTAINER_ROUNDED}
        testID={tID('ReservationSummary')}
      >
        <Spinner color={variant === 'complete' ? 'brand.primary' : 'white'} />
      </Box>
    );
  }

  if (variant === 'complete') {
    return <CompleteReservationSummary reservation={reservation} rt={rt} />;
  }

  const handleViewTickets = () => {
    if (reservation.hasTicketCategories) {
      if (reservation.ticketCategories.length === 1) {
        setSelectedTicketCategory(reservation.ticketCategories[0]);
        ticketsModal.onOpen();
      } else {
        setSelectedTicketCategory(undefined);
        ticketCategoryDialog.onOpen();
      }
    } else {
      ticketsModal.onOpen();
    }
  };

  const handleTicketsClose = () => {
    if (ticketCategoryDialog.isOpen) ticketCategoryDialog.onClose();
    ticketsModal.onClose();
  };

  const handleModify = () => {
    navigation.navigate('ReservationFlow', {
      attractionKey: attractionContent.key ?? '',
      productKey: productContent.key ?? '',
      attractionName: attractionContent?.name ?? '',
      reservationKey: reservation.reservationKey ?? '',
      isModification: true,
    });
  };

  const handleCancel = () => {
    onModifyCancel?.(reservation, 'cancelOnly');
  };

  const handleModifyCancel = () => {
    Analytics.trackEvent(Events.AttractionModifyTickets, {
      productKey: productContent?.key ?? '',
      attractionKey: attractionContent?.key ?? '',
    });

    onModifyCancel?.(reservation, 'modifyCancel');
  };

  return (
    <Box
      bg={CONTAINER_COLOR}
      px="4"
      py="6"
      rounded={CONTAINER_ROUNDED}
      testID={tID('ReservationSummary')}
    >
      <VStack alignItems="center" justifyContent="center" space="6">
        {isUpgradeReservation && (
          <Box alignItems="center" bg="white" borderRadius="4" p="2" w="full">
            <HStack alignItems="center" space="1">
              <Sunset />
              <Text
                color="black"
                fontFamily="ProximaNova"
                fontSize="10"
                fontWeight="400"
                lineHeight="13"
              >
                {upgradeBannerText}
              </Text>
            </HStack>
          </Box>
        )}
        {ticketSummaries.map(({ key, ticketCategory, optionTitle, option }, i) => (
          <VStack
            key={key}
            alignItems="center"
            borderBottomColor="white"
            borderBottomWidth={i < ticketSummaries.length - 1 ? 1 : 0}
            pb="6"
            space="4"
            w="full"
          >
            {!!optionTitle && (
              <ReservationDetailOption
                layout="horizontal"
                option={option}
                rt={rt}
                title={optionTitle}
              />
            )}

            <ReservationDetails
              passes={reservation.passes}
              reservation={reservation}
              rt={rt}
              ticketCategory={ticketCategory}
            />
          </VStack>
        ))}
        {showBtns && (
          <VStack space="2" w="full">
            {canShowViewTickets && (
              <Button variant="solidInverted" onPress={handleViewTickets}>
                {rt('app_view_tickets', { all: true })}
              </Button>
            )}
            {canShowCancelModifyButtons && (
              <>
                {canCancel && canModify && (
                  <Button variant="linkInverted" onPress={handleModifyCancel}>
                    {t('app_modify_cancel_reservation')}
                  </Button>
                )}

                {canCancel && !canModify && (
                  <Button variant="linkInverted" onPress={handleCancel}>
                    {rt('reservation_action_cancel')}
                  </Button>
                )}

                {canModify && !canCancel && (
                  <Button variant="linkInverted" onPress={handleModify}>
                    {rt('reservation_action_modify')}
                  </Button>
                )}
              </>
            )}
          </VStack>
        )}
      </VStack>

      {ticketsModal.isOpen && (
        <TicketCarouselModal
          isOpen={ticketsModal.isOpen}
          orderNumber={orderNumber}
          passes={reservation.passes}
          reservation={reservation}
          ticketCategory={reservation.hasTicketCategories ? selectedTicketCategory : undefined}
          onClose={handleTicketsClose}
        />
      )}

      {reservation.hasTicketCategories && ticketCategoryDialog.isOpen && (
        <RadioChoiceDialog
          body={rt('app_ticket_category_dialog_intro')}
          isOpen={ticketCategoryDialog.isOpen}
          items={ticketSummaries.map((summary) => ({
            value: summary,
            title: summary.optionTitle,
            body: <HTML html={summary.optionDetails} />,
          }))}
          title={rt('app_ticket_category_dialog_title')}
          onChange={(value) => {
            try {
              setSelectedTicketCategory(JSON.parse(value).ticketCategory);
              ticketsModal.onOpen();
            } catch (err) {
              Logger.error('failed to parse selected ticket category on selection', {
                err,
                value,
                reservationKey: reservation.reservationKey,
              });
            } finally {
              setTimeout(() => ticketCategoryDialog.onClose(), 500);
            }
          }}
          onClose={ticketCategoryDialog.onClose}
        />
      )}
      {/* <Dump data={{ now: dayjs().format('YYYY-MM-DD HH:mm:ss Z'), reservation }} /> */}
    </Box>
  );
}

function CompleteReservationSummary({
  reservation,
  rt,
}: {
  reservation: EntitlementReservation;
  rt: ReturnType<typeof useReservationTextTranslationHelper>;
}) {
  const addOnsQuantity = reservation.tickets?.filter((ticket) => isAddonTicket(ticket)).length ?? 0;
  const travelerCount = reservation.passes.length + addOnsQuantity;
  const travelerKey =
    travelerCount === 1 ? 'reservation_summary_traveler' : 'reservation_summary_travelers';
  const travelerFallback = `${travelerCount} ${travelerCount === 1 ? 'Traveler' : 'Travelers'}`;
  const reservationDate = ReservationHelper.date(reservation.reservationDateTime).format(
    'dddd, MMMM D, YYYY'
  );
  const ticketCategory = reservation.hasTicketCategories
    ? reservation.ticketCategories[0]
    : undefined;
  const shouldShowTime = ReservationHelper.showReservationTime(reservation, ticketCategory);
  const formatTime = (value: string) =>
    ReservationHelper.time(value, reservation.attractionConfig.ianaTimezone).format('h:mm A z');

  const preArrivalLabel = rt('pre_arrival_label');
  const departureLabel = rt('departure_label');
  const boardingTime = reservation.preArrivalTime;
  const showBoardingTimes = !!boardingTime && !!preArrivalLabel && !!departureLabel;

  const reservationTime = shouldShowTime ? formatTime(reservation.reservationDateTime) : '';
  const timeValue =
    showBoardingTimes && boardingTime
      ? [
          `${preArrivalLabel} \u2013 ${formatTime(boardingTime)}`,
          `${departureLabel} \u2013 ${reservationTime}`,
        ]
      : reservationTime;
  // The default note lives at the general level and applies to every boat attraction; an attraction
  // can override it, or opt in without boarding times, by defining the same key on itself.
  const timeNote = showBoardingTimes
    ? rt('reservation_time_note')
    : rt('reservation_time_note', { fallback: false });
  const optionLabel = reservation.option
    ? rt(`step_title_option_${reservation.option.optionType}`) || reservation.option.optionType
    : '';
  const optionValue = reservation.option
    ? rt(`${reservation.option.contentKey}_title`) || reservation.option.contentKey
    : '';

  return (
    <VStack space="3" testID={tID('ReservationSummaryComplete')}>
      <ReservationIconDetail
        icon={Person}
        label={rt('reservation_summary_travel_party') || 'Travel Party'}
        value={rt(travelerKey, { count: travelerCount }) || travelerFallback}
      />
      {!!reservation.option && (
        <ReservationIconDetail icon={Location} label={optionLabel} value={optionValue} />
      )}
      <ReservationIconDetail
        icon={Calendar}
        label={rt('step_title_date') || 'Date'}
        value={reservationDate}
      />
      {shouldShowTime && (
        <ReservationIconDetail
          icon={Clock}
          label={rt('step_title_time') || 'Time'}
          note={timeNote}
          value={timeValue}
        />
      )}
    </VStack>
  );
}
